import dayjs from "dayjs"

import type { Event } from "../../containers/Calendar/types/types.ts"

function getDateSafe(date: unknown) {
  if (
    !date ||
    typeof date === "boolean" ||
    (typeof date !== "string" &&
      typeof date !== "number" &&
      !(date instanceof Date) &&
      !dayjs.isDayjs(date))
  ) {
    return null
  }

  // A Dayjs has already picked its frame — re-zoning it here is what dragged an
  // all-day date back a day on the way into the edit form.
  if (dayjs.isDayjs(date)) {
    return date.isValid() ? date : null
  }

  const parsedDate = dayjs(date).tz()

  return parsedDate.isValid() ? parsedDate : null
}

function getFormatDate(date: unknown, format = "MMMM D, YYYY") {
  if (
    !date ||
    typeof date === "boolean" ||
    (typeof date !== "string" &&
      typeof date !== "number" &&
      !(date instanceof Date) &&
      !dayjs.isDayjs(date))
  ) {
    return null
  }

  const parsedDate = dayjs(date).tz()

  return parsedDate.isValid() ? parsedDate.format(format) : null
}

// All-day rows arrive under two conventions: canonical UTC midnight, and older
// rows carrying a local midnight. The anchor keeps both edges of one event in
// the frame its start picked.
function readAllDayDate(value?: string, anchor: string | undefined = value) {
  const local = dayjs(anchor).tz()
  const isLocalMidnight =
    local.hour() === 0 && local.minute() === 0 && local.second() === 0

  return isLocalMidnight ? dayjs(value).tz() : dayjs.utc(value)
}

const getEventDate = (event: Event, dateFormat?: string | null) => {
  const start = event?.start_at
  const end = event?.end_at

  const resolvedFormat = dateFormat ?? "MMMM D, YYYY"

  if (!start) return getFormatDate(start, resolvedFormat)

  const read = (value?: string) =>
    event?.all_day ? readAllDayDate(value, start) : dayjs(value).tz()

  const startDate = read(start)
  if (!startDate.isValid()) return getFormatDate(start, resolvedFormat)

  if (!end) return startDate.format(resolvedFormat)

  const endDate = read(end)
  if (!endDate.isValid() || startDate.isSame(endDate, "day")) {
    return startDate.format(resolvedFormat)
  }

  return `${startDate.format(resolvedFormat)} - ${endDate.format(resolvedFormat)}`
}

export { getDateSafe, getFormatDate, getEventDate, readAllDayDate }
